import React from 'react';
import _ from 'lodash';
import { getImage } from 'gatsby-plugin-image';
import { graphql } from 'gatsby';

import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import createEmotionStyles from '@src/utils/createEmotionStyles';

import Helmet from '@src/components/Helmet';
import LocalizedLink from '@src/components/LocalizedLink';
import Pagination from '@src/components/Pagination';
import Separator from '@src/components/Separator';
import Thumb from '@src/components/Thumb';
import emptyRightOrnament from '@src/assets/ornaments/landing-healthy-right.svg';
import heroLeftOrnament from '@src/assets/ornaments/community-stories.svg';
import newsTopRightOrnament from '@src/assets/ornaments/landing-upgrade.svg';

import config from '../../config.json';

const useStyles = createEmotionStyles((theme) => {
  const tpbp = theme.palette.background.paper;
  const tpbd = theme.palette.background.default;
  return {
    heroLeftOrnament: {
      right: '-20%',
      bottom: '50%',
      opacity: 0.33,
      width: '75%',
      transform: 'translateY(50%) rotate(25deg)',
      [theme.breakpoints.up('sm')]: {
        right: '-20%',
        width: '80%',
      },
      [theme.breakpoints.up('md')]: {
        left: '-33%',
        right: 'auto',
        width: 'auto',
      },
      [theme.breakpoints.up('lg')]: {
        opacity: 1,
        left: '-25%',
        right: 'auto',
        width: 'auto',
      },
      [theme.breakpoints.up('xl')]: {
        left: '-10%',
      },
    },
    newsTopRightOrnament: {
      right: 0,
      opacity: 0.75,
      top: '0%',
      transform: 'translateX(50%)',
      width: '75%',
      [theme.breakpoints.up('sm')]: {
        width: 'auto',
      },
    },
    newsIntro: {
      paddingBottom: theme.spacing(3),
      [theme.breakpoints.up('sm')]: {
        paddingBottom: theme.spacing(6),
      },
      [theme.breakpoints.up('lg')]: {
        paddingBottom: theme.spacing(9),
      },
    },
    news: {
      listStyle: 'none',
      margin: 0,
      marginTop: theme.spacing(3),
      [theme.breakpoints.up('sm')]: {
        marginTop: theme.spacing(6),
      },
      [theme.breakpoints.up('lg')]: {
        marginTop: theme.spacing(9),
      },
      padding: 0,
      '& > *': {
        marginTop: theme.spacing(3),
        [theme.breakpoints.up('sm')]: {
          marginTop: theme.spacing(6),
        },
        [theme.breakpoints.up('lg')]: {
          marginTop: theme.spacing(9),
        },
      },
    },
    body: {
      backgroundImage: `linear-gradient(to bottom, ${tpbp} 0%, ${tpbd} 100%)`,
    },
    emptyRightOrnament: {
      bottom: 0,
      right: '-25%',
      [theme.breakpoints.up('md')]: {
        right: 0,
      },
    },
    takeawayCta: {
      marginTop: theme.spacing(2),
    },
    title: {
      marginTop: theme.spacing(2),
    },
  };
});

export default function NewsTpl({
  pageContext: { locale, ...pageContext },
  ...props
}) {
  const classes = useStyles();
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  // const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isXlUp = useMediaQuery(theme.breakpoints.up('xl'));

  const page = props.data.page.frontmatter[locale];
  const allNews = props.data.news.edges.map(({ node }) => {
    const { slug } = _.find(node.fields.slugs, (o) => o.locale === locale);
    return {
      ...node.frontmatter[locale],
      slug,
    };
  });
  const collection = _.find(config.collections, (c) => c.id === 'news');
  const { requirements } = collection;
  const news = _.filter(allNews, (s) =>
    _.every(requirements, (r) => s[r] !== null)
  );

  // pagination
  const { currentPage, numPages } = pageContext;
  const isFirst = currentPage === 1;
  const isLast = currentPage === numPages;
  const prevPage =
    currentPage - 1 === 1
      ? `/${collection.parentSlug[locale]}`
      : `/${collection.parentSlug[locale]}/${currentPage - 1}`;
  const nextPage = `/${collection.parentSlug[locale]}/${currentPage + 1}`;

  return (
    <>
      <Helmet title={page.title} />

      <main className={classes.root}>
        <header className={`${classes.hero} block`}>
          <div>
            <div className={`${classes.heroLeftOrnament} ornament`}>
              <img aria-hidden="true" src={heroLeftOrnament} alt="" />
            </div>
            <Container
              className="container"
              maxWidth={isXlUp ? 'md' : isSmUp ? (isMdUp ? 'md' : 'sm') : 'xs'}
            >
              <Typography className="hero-title" component="p" variant="h1">
                {page.hero.title}
              </Typography>
              <Typography
                className="hero-text"
                component="p"
                variant="subtitle2"
              >
                {page.hero.text}
              </Typography>
            </Container>
          </div>
        </header>

        <Separator
          from={theme.palette.background.default}
          to={theme.palette.background.paper}
          weight="right"
        />

        <section className={`${classes.body} block block--1of2`}>
          <div>
            <div className={`${classes.emptyRightOrnament} ornament`}>
              <img aria-hidden="true" src={emptyRightOrnament} alt="" />
            </div>
            <div className={`${classes.newsTopRightOrnament} ornament`}>
              <img aria-hidden="true" src={newsTopRightOrnament} alt="" />
            </div>

            {Boolean(news) && news.length > 0 && (
              <Container
                className="container"
                maxWidth={isMdUp ? 'sm' : isSmUp ? 'sm' : 'xs'}
              >
                <ul className={classes.news}>
                  {news.map(({ cover, date, slug, summary, title }) => (
                      <li key={slug}>
                        <article>
                          <LocalizedLink to={`/${slug}`}>
                            <Thumb alt={title} image={getImage(cover)} />
                          </LocalizedLink>
                          <Typography
                            component="h2"
                            gutterBottom
                            className={classes.title}
                          >
                            <Link
                              color="textPrimary"
                              component={LocalizedLink}
                              gutterBottom
                              to={`/${slug}`}
                              variant="subtitle1"
                            >
                              {title}
                            </Link>
                          </Typography>
                          <Typography
                            className={classes.excerpt}
                            gutterBottom
                            variant="body1"
                          >
                            {summary}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {date}
                          </Typography>
                        </article>
                      </li>
                    ))}
                </ul>
                <Separator />
                <Pagination
                  isFirst={isFirst}
                  isLast={isLast}
                  currentPage={currentPage}
                  numPages={numPages}
                  prevPage={prevPage}
                  nextPage={nextPage}
                />
              </Container>
            )}
          </div>
        </section>
      </main>
    </>
  );
}

export const query = graphql`
  query NewsTplQuery(
    $dateFormat: String!
    $id: String!
    $limit: Int!
    $skip: Int!
  ) {
    page: markdownRemark(id: { eq: $id }) {
      frontmatter {
        en {
          hero {
            title
            text
          }
          title
          description
        }
        es {
          hero {
            title
            text
          }
          description
          title
        }
      }
    }
    news: allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/site/collections/news/.*md$/" } }
      limit: $limit
      skip: $skip
      sort: { frontmatter: { en: { date: DESC } } }
    ) {
      edges {
        node {
          fields {
            slugs {
              locale
              slug
            }
          }
          frontmatter {
            en {
              date(formatString: $dateFormat)
              summary
              title
              cover {
                childImageSharp {
                  gatsbyImageData(
                    formats: [AUTO, WEBP]
                    layout: FULL_WIDTH
                    placeholder: BLURRED
                  )
                }
              }
            }
            es {
              date(formatString: $dateFormat)
              summary
              title
              cover {
                childImageSharp {
                  gatsbyImageData(
                    formats: [AUTO, WEBP]
                    layout: FULL_WIDTH
                    placeholder: BLURRED
                  )
                }
              }
            }
          }
        }
      }
    }
  }
`;
